import { useState } from "react";
import classnames from "classnames";

import { TextLink } from "components/actions/Links";
import { RouterTextLink } from "components/actions/RouterLink";
import { SaveLeadDrawerProvider } from "components/leads/saving/SaveLeadDrawerProvider";
import ShareModal from "components/modals/ShareModal";
import { NoticeFilters, recordDetailsUrl } from "components/notices/utils";
import Alert from "components/ui/alert/Alert";
import { appRoutes } from "lib/appRoutes";
import NoticeStage from "lib/core_components/NoticeStage";
import { TextHighlighter } from "lib/core_components/TextHighlighter";
import { NOTICE_INSIGHTS_RAIL, useVariableValue } from "lib/featureFlags";
import { useNoticeListsForRecord } from "lib/hooks/api/notices/lists/useNoticeListsForRecord";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { toAndSearch } from "lib/spaNavigation";
import { EventNames, useTracking } from "lib/tracking";
import { getSignalTypes } from "lib/types/models";
import { assertDefined, isOpenEndedDate } from "lib/utils";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import {
  NoticesStages as Stages,
  NoticesStagesv2,
  NoticeTimelineStage,
} from "../../lib/types/graphQLEnums";
import { Flex } from "../../styles/utility-components";
import { ViewLeadsDrawerProvider } from "../leads/viewing/ViewLeadsDrawerProvider";
import { NoticeActions } from "../notices/NoticeActions";
import { NoticeHeaderDropdown } from "./NoticeHeaderDropdown";
import { NoticeSeenBy } from "./SeenBy";
import { getLatestSourceNoticeDate } from "./SourceNoticesProperty";

import css from "./NoticeHeader.module.scss";

function getRecordDate(notice: NoticeDetails) {
  switch (notice.stage) {
    case Stages.Awarded:
      return notice.expiryDate;
    case Stages.Open:
    case Stages.Closed:
      return notice.closeDate;
    default:
      return notice.publishDate;
  }
}

function getRelativeTimeLabel(stage: Stages, isFuture: boolean) {
  switch (stage) {
    case Stages.Awarded:
      return isFuture ? "Expiring" : "Expired";
    case Stages.Open:
    case Stages.Closed:
      return isFuture ? "Closing" : "Closed";
    default:
      return "Published";
  }
}

function getEmptyDateLabel(stage: Stages) {
  switch (stage) {
    case Stages.Awarded:
      return "expiry";
    case Stages.Open:
    case Stages.Closed:
      return "close";
    default:
      return "publish";
  }
}

function getLatestStage(timeline: NoticeDetails["timeline"]): NoticeTimelineStage | null {
  const latestAward = timeline?.award.find(
    (award) => award.estimated === false && award.publishedDate !== null,
  );
  if (latestAward) {
    return NoticeTimelineStage.Award;
  }
  if (timeline?.tender && timeline.tender?.estimated === false) {
    return NoticeTimelineStage.Tender;
  }
  if (timeline?.preTender) {
    return NoticeTimelineStage.PreTender;
  }
  // Should be impossible with the current logic, but just in case
  return null;
}

export function getRelativeTimeFromNow(notice: NoticeDetails): string | null {
  assertDefined(notice.stage);

  const latestNoticeDate = getLatestSourceNoticeDate(notice.sourceNotices);
  const recordDate = latestNoticeDate ?? getRecordDate(notice);

  if (!recordDate) {
    return `Unknown ${getEmptyDateLabel(notice.stage)} date`;
  }

  const dateObj = new Date(recordDate);
  const isFuture = dateObj.getTime() > Date.now();
  const label = latestNoticeDate ? "Latest notice" : getRelativeTimeLabel(notice.stage, isFuture);

  if (isOpenEndedDate(dateObj)) {
    return "Open-ended";
  }

  const timeFromNow = toHumanRelativeDateFromNow(recordDate);
  return `${label} ${timeFromNow}`;
}

type NoticeHeaderProps = {
  notice: NoticeDetails;
  headerHighlights: string[];
  trackingContextName:
    | "Title header"
    | "Record preview slide-out"
    | "In-table row"
    | "In-table dropdown";
  disableTitleLink?: boolean;
  showStage?: boolean;
  headerClassName?: string;
  scrollClassName?: string;
  isTopOfPageVisible?: boolean;
  // Filters used here to translate active filters to eventually
  // become a search request for highlighting API in notice details page
  filters?: NoticeFilters;
};

export default function NoticeHeader({
  notice,
  filters,
  headerHighlights,
  trackingContextName,
  disableTitleLink,
  showStage = true,
  headerClassName,
  scrollClassName,
  isTopOfPageVisible,
}: NoticeHeaderProps) {
  assertDefined(notice.stage);

  const [shareModalOpen, setShareModalOpen] = useState(false);

  const { logEvent } = useTracking();

  // Warms the "Add to list" drawer so its saved lists show as soon as it opens.
  useNoticeListsForRecord(notice.guid);
  const railEnabled = useVariableValue(NOTICE_INSIGHTS_RAIL, false);

  const onOpenChange = (open: boolean) => {
    setShareModalOpen(open);
    if (open) {
      logEvent(EventNames.shareEmailModalOpen, { "Record GUIDs": [notice.guid] });
    }
  };

  const headerClass = classnames(
    headerClassName,
    scrollClassName && !isTopOfPageVisible && scrollClassName,
  );

  const timeFromNowLabel = getRelativeTimeFromNow(notice);

  const latestStageForNotice = getLatestStage(notice.timeline);
  // A timeline stage can be described by the OCDS release without a notice existing for it.
  const latestStageNotice =
    latestStageForNotice && notice.procurementStage.stage !== latestStageForNotice
      ? notice.stageNotices.find((stageNotice) => stageNotice.stage === latestStageForNotice)
      : undefined;

  const noticeSuppliers = Array.from(
    new Set(notice.lots.flatMap((lot) => lot.suppliers?.map((supplier) => supplier.guid) || [])),
  );

  return (
    <SaveLeadDrawerProvider trackingData={{ "Context source": "Notice header" }}>
      <ViewLeadsDrawerProvider>
        <div className={headerClass}>
          <div className={css.headerColumn}>
            <Flex gap={48} justifyContent="space-between" alignItems="center">
              <Flex flexDirection="column" gap={16}>
                {showStage && (
                  <div className={css.stage}>
                    <NoticeStage stage={notice.stagev2 ?? undefined} />
                    <div className={css.date}>
                      •<div>{timeFromNowLabel}</div>
                    </div>
                  </div>
                )}

                <div aria-label="Record link">
                  <RouterTextLink
                    className={css.title}
                    {...appRoutes.noticeDetail(notice.guid)}
                    search={toAndSearch(recordDetailsUrl(notice.guid, filters)).search}
                    disabled={disableTitleLink}
                  >
                    <TextHighlighter
                      content={notice.name ?? ""}
                      wordsToHighlight={headerHighlights}
                    />
                  </RouterTextLink>
                  <NoticeSeenBy
                    seenByUsers={notice.seenByUsers}
                    displayNumber={true}
                    className={css.seenBy}
                  />
                </div>
              </Flex>
              <div className={css.actions}>
                <NoticeActions
                  notice={{
                    id: notice.guid,
                    name: notice.name ?? "",
                    value: notice.value ?? undefined,
                    score: notice.score,
                    signalTypes: getSignalTypes(notice.signals ?? []),
                    procurementStage: {
                      id: notice.procurementStage.id,
                      stage: notice.procurementStage.stage
                        ? notice.procurementStage.stage.toString()
                        : "",
                    },
                    isHidden: notice.isHidden,
                    stage: notice.stage,
                    stageV2: notice.stagev2 || undefined,
                    closeDate: notice.closeDate || undefined,
                    expiryDate: notice.expiryDate || undefined,
                    buyers: notice.buyers.map((buyer) => buyer.guid),
                    suppliers: noticeSuppliers,
                    leads: notice.leads,
                    bid: notice.bid || undefined,
                    hasBuyerLeads: notice.hasBuyerLeads || undefined,
                  }}
                  contextSource={trackingContextName}
                  // With the rail there's no CTA card, so the header offers its report.
                  canCreateBidReport={railEnabled && notice.stagev2 === NoticesStagesv2.OpenTender}
                />
                <NoticeHeaderDropdown
                  onShareClick={() => onOpenChange(true)}
                  notice={{ ...notice, suppliers: noticeSuppliers }}
                />
                <ShareModal
                  open={shareModalOpen}
                  onOpenChange={onOpenChange}
                  record={{
                    guid: notice.guid,
                    signals: notice.signals ?? [],
                    stage: notice.stage,
                    score: notice.score ?? 0,
                  }}
                />
              </div>
            </Flex>

            {latestStageNotice && (
              <Alert type="info">
                This is no longer the latest notice for this process.{" "}
                <TextLink to={recordDetailsUrl(latestStageNotice.guid)}>
                  Click here to view the most recent notice.
                </TextLink>
              </Alert>
            )}
          </div>
        </div>
      </ViewLeadsDrawerProvider>
    </SaveLeadDrawerProvider>
  );
}
